import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { useRecordCampaignPresetOriginApi } from 'src/api/campaignPresets';
import { Campaign, CampaignExternalType, CampaignStatus } from '../types';
import { CampaignConfigSchema } from '../types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { CampaignPolicy, usePolicyApi } from '../policies';
import { applyPresetToDefaults } from '../helpers';
import { CampaignFormValues, SaveMode } from '../types';
import { useCampaignFormState } from './useCampaignFormState';
import dayjs from 'dayjs';

export interface SubmitOptions {
  /** Skip the redirect to /campaigns; the caller navigates somewhere else. */
  preventNavigation?: boolean;
  /** Backend reported the company is not ready to activate (onboarding/subscription). */
  onFailedDependency?: () => void;
  /** Runs after persistence but before reset/navigation (activation uses this). */
  afterPersist?: (savedCampaign: Campaign | undefined) => Promise<void> | void;
  /**
   * Persist these fields without putting them into the visible source form.
   * Saving a live campaign as a new draft uses this for its generated copy name.
   */
  valuesOverride?: Partial<CampaignFormValues>;
  /** A draft payload being activated still has to pass the full activation schema. */
  validateAsActive?: boolean;
}

interface UseCampaignFormParams {
  /** Embedded builders return the saved campaign instead of navigating away. */
  onSaved?: (campaign: Campaign) => void;
  /** Full-page builders leave for Campaigns after a save; this is the router state they carry there. */
  saveRedirectState?: (campaign: Campaign) => unknown;
  /** Inline creation starts with a blank editor instead of configured message copy. */
  startWithEmptyMessages?: boolean;
  /**
   * Overrides the policy's external type. The policy derives it from the config, and an
   * application's config always yields `custom`, which a host that needs a fixed type cannot use.
   */
  externalType?: CampaignExternalType;
  policy: CampaignPolicy;
  config: CampaignConfigSchema;
  configId: string | null;
  campaign?: Campaign | null;
  /** Preset the builder was launched from, when starting from a saved template. */
  preset?: CampaignPreset | null;
  /** When true, always use 'create' mode even if campaignId is present (duplicate flow). */
  forceCreate?: boolean;
  /**
   * Pre-resolved unique name for the duplicate / preset flows. The loader
   * resolves it before the builder renders, so the form is created with its
   * final name — applying it later via `reset` would clear the dirty flag and
   * silently swallow whatever the user typed in the meantime.
   */
  initialName?: string | null;
  preselectedAppointmentTypeId?: string;
  preselectedGroupId?: string;
  preselectedGroupName?: string;
}

export function useCampaignForm({
  policy, config, configId, campaign, preset, forceCreate, initialName, onSaved, saveRedirectState,
  preselectedAppointmentTypeId, preselectedGroupId, preselectedGroupName, startWithEmptyMessages, externalType,
}: UseCampaignFormParams) {
  const companyId = useSelector(getCurrentCompanyId) as string;
  const { save } = usePolicyApi();
  const recordPresetOrigin = useRecordCampaignPresetOriginApi();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  // If activation fails after creation, retry updates that record instead of
  // creating another campaign with the same name.
  const embeddedCampaignId = useRef<string>();

  const defaultValues = useMemo<CampaignFormValues>(() => {
    const base = campaign
      ? policy.fromCampaign(campaign, config)
      : (() => {
          const defaults = policy.getDefaults(config);
          const seeded = preset ? applyPresetToDefaults(defaults, preset, config) : defaults;
          return startWithEmptyMessages
            ? {
                ...seeded,
                scheduledMessages: seeded.scheduledMessages.slice(0, 1).map(message => ({
                  ...message, text: '', subject: '', links: [], isSurvey: false,
                })),
              }
            : seeded;
        })();
    const preselected = campaign ? base : {
      ...base,
      ...(preselectedAppointmentTypeId && config.type === 'reminder'
        ? { appointmentTypeId: preselectedAppointmentTypeId }
        : {}),
      ...(preselectedGroupId && config.type !== 'recurring'
        ? { groupIds: [preselectedGroupId] }
        : {}),
      ...(preselectedGroupName
        ? { name: `${dayjs().format('YYYY-MM-DD')} - ${preselectedGroupName} Message` }
        : {}),
    };
    // Duplicate / preset flows arrive with their unique name already resolved.
    return initialName ? { ...preselected, name: initialName } : preselected;
  }, [policy, config, campaign, preset, initialName, preselectedAppointmentTypeId,
    preselectedGroupId, preselectedGroupName, startWithEmptyMessages]);

  const validator = useCallback(
    (values: CampaignFormValues) => policy.validate(values, config),
    [policy, config],
  );
  const form = useCampaignFormState(defaultValues, validator);

  const submit = useCallback(
    async (status: CampaignStatus, mode: SaveMode, options?: SubmitOptions) => {
      const persist = async (formValues: CampaignFormValues) => {
        const effectiveMode: SaveMode = onSaved && embeddedCampaignId.current ? 'update' : forceCreate ? 'create' : mode;
        const payloadValues = options?.valuesOverride
          ? { ...formValues, ...options.valuesOverride }
          : formValues;
        const payload = {
          ...policy.toPayload(payloadValues, config, status),
          configId,
          ...(externalType ? { externalType } : {}),
        };

        let result;
        try {
          result = await save(payload, effectiveMode, { campaignId: embeddedCampaignId.current || campaign?.id, companyId });
        } catch (error) {
          // Anything not handled here is re-thrown, so a failed save never ends in
          // a silent redirect.
          const apiError = error as { error?: string };
          if (apiError?.error === 'conflict') {
            form.setFieldErrors({ name: 'Entity with this name already exists' });
            return;
          }
          if (apiError?.error === 'failed_dependency') {
            options?.onFailedDependency?.();
            return;
          }
          throw error;
        }

        // Record preset lineage on create only; never block creation.
        const savedCampaign = Array.isArray(result?.data) ? result.data[0] : result?.data;
        const newCampaignId = savedCampaign?.id;
        if (onSaved && newCampaignId) embeddedCampaignId.current = newCampaignId;
        if (preset && effectiveMode === 'create' && newCampaignId && companyId) {
          try {
            await recordPresetOrigin(newCampaignId, preset.id, companyId);
          } catch (e) {
            console.error('Failed to record preset origin', e);
          }
        }

        await options?.afterPersist?.(savedCampaign);

        // Clear dirty first or the unsaved-changes guard blocks the redirect.
        // `preventNavigation` is for callers that navigate somewhere else themselves.
        // Overrides belong to the new entity, not to the source campaign still
        // displayed by this form. Accept only the visible values as the baseline.
        if (!mounted.current) return result;
        form.reset(formValues);
        // Activation runs after the save, so the response still carries the pre-activation status.
        const finalCampaign = savedCampaign
          && { ...savedCampaign, status: options?.validateAsActive ? 'active' as const : status };
        if (onSaved) {
          if (finalCampaign) onSaved(finalCampaign);
        } else if (!options?.preventNavigation) {
          // An update answers with an empty body, so the campaign being edited stands in for it.
          // A duplicate's `campaign` is its source, which must never stand in for the new copy.
          const redirectCampaign = finalCampaign || (!forceCreate && campaign
            && { ...campaign, name: formValues.name, status: options?.validateAsActive ? 'active' as const : status });
          const state = redirectCampaign ? saveRedirectState?.(redirectCampaign) : undefined;
          setTimeout(() => navigate('/campaigns', { state }), 0);
        }
        return result;
      };

      // Plain drafts skip required-field activation rules — every policy expects
      // a fully filled campaign, so running them here made "Save as Draft" a
      // silent no-op. Activating an existing draft opts back into the full
      // schema while keeping `status: draft` in its pre-activation payload.
      const values = form.getValues();

      // Re-validate at click time rather than trusting the rendered state.
      const found = form.validate();

      // Both save paths count as attempted: a draft may still carry empty fields,
      // but errors that actually block it have to become visible.
      form.revealAll();

      const runPersist = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsSaving(true);
        try { return await persist(values); } finally {
          savingRef.current = false;
          setIsSaving(false);
        }
      };

      if (status === 'draft' && !options?.validateAsActive) {
        const blocking = found.filter(issue => issue.blocksDraft);
        if (blocking.length > 0) {
          form.setFieldErrors(Object.fromEntries(blocking.map(i => [i.path, i.message])));
          return;
        }
        return runPersist();
      }

      if (found.some(issue => issue.blocksPrimaryAction)) return;
      return runPersist();
    },
    [form, policy, config, configId, campaign, companyId, save, forceCreate, navigate, preset, recordPresetOrigin, onSaved,
      saveRedirectState, externalType],
  );

  return { form, submit, isDirty: form.isDirty, isSaving };
}
